import { getParameterByName } from '@utils/PageUtils';
import { RegionMappingService } from '@services/RegionMappingService';

export const enum DevicePlatform {
    Android = 'Android',
    Browser = 'Browser',
    SXClient = 'SXClient',
}
export const enum DeviceType {
    Android = 'handheld',
    Browser = 'desktop',
}
export interface IZoneType {
    zoneName: string;
    subzoneName: string;
}

export class EnvironmentDetail {
    public static isAndroid = (): boolean => {
        // Previous implementation checked for presence of Cordova but resulted in race conditions.
        // Use cordovaReady for that purpose instead.
        return !!window.navigator.userAgent.match(/ATv3/);
    };

    public static isWindowsMobile = (): boolean => {
        // Leaving this method defined here as to not break any tools that may use it.
        return false;
    };

    public static isBrowser = (): boolean => {
        return !EnvironmentDetail.isAndroid();
    };

    /**
     * Checks if the current environment is SXClient (Windows WebView2).
     * SXClient provides chrome.webview.hostObjects for communication
     * between JavaScript and the C# host application.
     */
    public static isSXClient = (): boolean => {
        if (typeof window === 'undefined') {
            return false;
        }

        const win = window as any;
        return !!win.chrome?.webview?.hostObjects;
    };

    public static isVelocityBrowser(_window: Window & typeof globalThis = window): _window is Window & typeof globalThis & { Velocity: object } {
        return typeof _window !== 'undefined' && _window !== null && 'Velocity' in _window && typeof (_window as any).Velocity === 'object' && (_window as any).Velocity !== null;
    }

    public static isIIBAuthN = (): boolean => {
        return EnvironmentDetail.getAuth() === 'iib' || EnvironmentDetail.isFCMenuAuthN();
    };

    public static isFCMenuAuthN(): boolean {
        return EnvironmentDetail.getAuth() === 'fcmenu';
    }

    public static isAFTXAuthN(): boolean {
        return EnvironmentDetail.getAuth() === 'aftx';
    }

    public static getLoginType = (): string => {
        return getParameterByName('loginType') || window.sessionStorage.getItem('loginType');
    };

    public static getRegion(): string {
        return EnvironmentDetail.normalizeRegion(getParameterByName('region') || window.sessionStorage.getItem('region'));
    }

    public static getRealm(): string {
        return getParameterByName('realm') || window.sessionStorage.getItem('realm') || EnvironmentDetail.getRealmFromRegion();
    }

    public static getDomain(): string {
        // Not setting domain for sessionStorage since it will be present in query param for 3P as well
        return getParameterByName('domain');
    }

    public static getStoreFamily(): string {
        return getParameterByName('storeFamily') || window.sessionStorage.getItem('storeFamily');
    }

    public static getStoreId(): string {
        return getParameterByName('storeId') || window.sessionStorage.getItem('storeId');
    }

    public static getStoreIdType(): string {
        return getParameterByName('storeIdType') || window.sessionStorage.getItem('storeIdType') || 'UNKNOWN';
    }

    public static getSuppressConnections(): string {
        return getParameterByName('suppressConnections') || window.sessionStorage.getItem('suppressConnections');
    }

    public static getInitialTool(): string {
        return getParameterByName('initialTool') || window.sessionStorage.getItem('initialTool');
    }

    public static getInitialZoneInfo(): IZoneType | null {
        const zoneName = getParameterByName('initialZoneName') || window.sessionStorage.getItem('initialZoneName');
        const subzoneName = getParameterByName('initialSubzoneName') || window.sessionStorage.getItem('initialSubzoneName');
        const initialZoneInfo = {
            zoneName,
            subzoneName,
        };
        if (!initialZoneInfo.zoneName || !initialZoneInfo.subzoneName) {
            return null;
        }
        return initialZoneInfo;
    }

    public static getMenuVersion(): string {
        return getParameterByName('menuVersion');
    }

    public static getDevicePlatform(): string {
        if (EnvironmentDetail.isAndroid()) {
            return DevicePlatform.Android;
        } else if (EnvironmentDetail.isSXClient()) {
            return DevicePlatform.SXClient;
        } else {
            return DevicePlatform.Browser;
        }
    }

    public static getDeviceType(): string {
        console.log('getDeviceType isVelocityBrowser' + EnvironmentDetail.isVelocityBrowser(window));

        if (EnvironmentDetail.isAndroid() || EnvironmentDetail.isVelocityBrowser(window)) {
            return DeviceType.Android;
        } else {
            return DeviceType.Browser;
        }
    }

    public static isIhmIdentityBrokerAuthN(): boolean {
        return EnvironmentDetail.getAuth() === 'ihm-identity-broker';
    }

    public static isAssociateToolsUi(): boolean {
        return EnvironmentDetail.ASSOCIATE_TOOLS_UI_HOST_REGEX.test(window.location.host);
    }

    public static getChromeVersion(): string | null {
        const userAgent = window.navigator.userAgent;
        // Sample User Agent String:
        // Mozilla/5.0 (Linux; Android 7.1.2; TC70x Build/01-01-49.00-NG-U20-STD; wv)
        // AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/79.0.3945.93 Mobile Safari/537.36 ATv3
        // We're interested in the "Chrome/79.0.3945.93" piece
        const chromeVersionResult = /chrome\/([^ ]+)/i.exec(userAgent);
        if (chromeVersionResult) {
            return chromeVersionResult[1];
        }

        return null;
    }

    public static getAuth(): string {
        return getParameterByName('auth');
    }

    private static ASSOCIATE_TOOLS_UI_HOST_REGEX = new RegExp(/^ui\..*\.associatetools\.ihm.amazon\.dev$/);

    private static getRealmFromRegion(): string | null {
        const region = EnvironmentDetail.getRegion();
        if (region && EnvironmentDetail.regionToRealmMap[region.toLocaleLowerCase()]) {
            return EnvironmentDetail.regionToRealmMap[region.toLocaleLowerCase()];
        }
        return null;
    }

    /**
     * Normalizes the region value to the canonical lowercase airport code used internally.
     *
     * Accepts both vocabularies for the `region` parameter:
     *   'pdx'       -> 'pdx'   (canonical form, unchanged)
     *   'PDX'       -> 'pdx'   (case-insensitive: FC Menu substitution fields emit uppercase)
     *   'us-west-2' -> 'pdx'   (AWS-region form: enables a single {awsRegion}-templated menu URL
     *                           to drive both the hostname and the region parameter)
     *
     * Values in neither vocabulary are returned unchanged so the existing
     * validation/failure paths behave exactly as before.
     */
    private static normalizeRegion(raw: string | null): string | null {
        if (!raw || raw.trim() === '') {
            return raw;
        }
        const value = raw.trim().toLowerCase();
        if (EnvironmentDetail.regionToRealmMap[value]) {
            return value;
        }
        try {
            const airportCode = RegionMappingService.getAirportCodeFromAwsRegion(value).toLowerCase();
            // Only accept AWS regions that map back to a region ATBase actually operates in.
            if (EnvironmentDetail.regionToRealmMap[airportCode]) {
                return airportCode;
            }
            return raw;
        } catch {
            // Expected for values that are not AWS-region names: getAirportCodeFromAwsRegion
            // throws on unknown input. Pass the raw value through unchanged so the existing
            // downstream validation/failure paths handle it exactly as before this change.
            return raw;
        }
    }

    // Update this for new region bringup
    private static regionToRealmMap = {
        iad: 'us-east-1',
        pdx: 'us-west-2',
        sin: 'ap-southeast-1',
        dub: 'eu-west-1',
        zaz: 'eu-south-2',
    };
}
